import { useEffect, useMemo, useRef, useState } from 'react';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import {
  refreshSession,
  setEmailNotVerifiedHandler,
  setLegalAcceptanceRequiredHandler,
  setSessionLostHandler,
} from '@/shared/api/client';
import { fetchProfile, type ConsumerProfile } from '@/entities/session/api';
import { AuthProvider, useAuth } from './auth';
import { createQueryClient, queryKeys } from './query';
import { router } from './router';
import { isGateExempt, LEGAL_ACCEPT_PATH } from '@/features/legal/gate';
import { CONFIRM_EMAIL_PATH } from '@/features/profile/emailGate';
import { BrandMark } from '@/shared/ui/Brand';

/**
 * Session restore (screen A1), then the router.
 *
 * This screen exists only in the web. Both mobile clients deliberately skip it and show the
 * login form on every cold start, which is tolerable when the app stays resident — but a PWA
 * genuinely reloads, and «sign in again every time» would read as a regression rather than as a
 * missing extra.
 *
 * The router is mounted only after the restore settles. Rendering it first and letting route
 * guards resolve asynchronously would flash the login screen at every returning visitor, which
 * is exactly the impression this whole screen exists to avoid.
 */
/** How long the branded hold may last before the app gives up and shows the login screen. */
const RESTORE_TIMEOUT_MS = 6_000;

export function App() {
  const queryClient = useMemo(() => createQueryClient(), []);
  const [restored, setRestored] = useState(false);
  const [profile, setProfile] = useState<ConsumerProfile | null>(null);

  useEffect(() => {
    let cancelled = false;

    /**
     * The restore is bounded. Without a deadline a request that never answers — a captive
     * portal, a dead tunnel, a phone that woke up with no signal — leaves the app on the
     * loading mark indefinitely, with no button and nothing to read. Falling through to the
     * login screen after a few seconds is recoverable; an eternal splash is not.
     */
    const deadline = new Promise<'timeout'>((resolve) => {
      window.setTimeout(() => resolve('timeout'), RESTORE_TIMEOUT_MS);
    });

    void (async () => {
      const restore = (async () => {
        // With no session at all this simply returns false and we fall through to /login.
        if (!(await refreshSession())) return;
        try {
          const me = await fetchProfile();
          if (!cancelled) setProfile(me);
        } catch {
          // A refresh that works but a profile that does not is a server-side problem, not a
          // reason to claim the person is signed out. They land on /login and can try again.
        }
      })();

      await Promise.race([restore, deadline]);
      if (!cancelled) setRestored(true);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!restored) return <RestoringScreen />;

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider initialProfile={profile}>
        <RoutedApp />
      </AuthProvider>
    </QueryClientProvider>
  );
}

/**
 * Bridges the auth state into the router's context, so route guards can read it synchronously,
 * and turns a session that dies mid-flight into a clean trip back to the login screen.
 */
function RoutedApp() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    setSessionLostHandler(() => {
      auth.forgetSession();
      void router.navigate({ to: '/login', replace: true });
    });
    return () => setSessionLostHandler(null);
  }, [auth]);

  /**
   * Route guards read `auth` synchronously in `beforeLoad`, which runs on NAVIGATION — so a
   * change to who is signed in, with nobody navigating, leaves the screen the previous answer
   * put there. That is the case a late session restore lands in: the person was already sent to
   * the login screen, the profile arrives a second later, and without this they stay there
   * looking at a form they no longer need. Re-running the guards moves them on.
   *
   * Только на СМЕНУ, не на первый рендер: маршрутизатор в этот момент ещё разбирается с первым
   * переходом, и `invalidate` поверх него сбивает его же навигацию.
   */
  const previousStatus = useRef(auth.status);
  useEffect(() => {
    if (previousStatus.current === auth.status) return;
    previousStatus.current = auth.status;
    void router.invalidate();
  }, [auth.status]);

  /**
   * The safety net under the legal gate.
   *
   * The gate itself runs before a protected screen renders, but it cannot cover the case that
   * matters most: a new redaction published while somebody is halfway through ordering. Then the
   * refusal arrives as `legal_acceptance_required` on the order request itself, and this is what
   * turns it into the same screen — from anywhere, without every call site having to know.
   *
   * What we believed about `pending` is dropped first: the server has just said we were wrong,
   * so the acceptance screen must not open against a cached «нечего принимать» and immediately
   * bounce back. And the person is returned to the URL they were interrupted on, which is where
   * an order draft lives.
   */
  useEffect(() => {
    setLegalAcceptanceRequiredHandler(() => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.legalPending });

      const here = router.state.location;
      if (isGateExempt(here.pathname)) return;
      void router.navigate({ to: LEGAL_ACCEPT_PATH, search: { redirect: here.href } });
    });
    return () => setLegalAcceptanceRequiredHandler(null);
  }, [queryClient]);

  /**
   * То же самое для неподтверждённого адреса почты.
   *
   * Гейт нельзя поставить перед экраном: подсказка в профиле может быть несвежей, а решение
   * принимает сервер в момент сделки. Поэтому отказ приходит на самом запросе заказа, и здесь он
   * превращается в экран подтверждения — из любого места, не заставляя каждый вызов знать про
   * этот случай.
   *
   * Человек возвращается на URL, с которого его прервали: там живёт черновик заказа, и терять его
   * из-за одного подтверждения было бы худшим из возможного.
   */
  useEffect(() => {
    setEmailNotVerifiedHandler(() => {
      const here = router.state.location;
      if (here.pathname === CONFIRM_EMAIL_PATH) return;
      void router.navigate({ to: CONFIRM_EMAIL_PATH, search: { redirect: here.href } });
    });
    return () => setEmailNotVerifiedHandler(null);
  }, []);

  /**
   * The end of a session forgets what that session had accepted.
   *
   * The cache survives a sign-out — the `QueryClient` is created once for the life of the tab —
   * and «этот аккаунт всё принял» is the one cached answer that must never be reused for
   * somebody else. Signing out and straight back in as another account would otherwise walk
   * past the gate on the previous person's answer for up to half a minute.
   */
  useEffect(() => {
    if (auth.status !== 'anonymous') return;
    queryClient.removeQueries({ queryKey: queryKeys.legalPending });
  }, [auth.status, queryClient]);

  return <RouterProvider router={router} context={{ auth, queryClient }} />;
}

/** A branded hold rather than a bare spinner — on a first visit this is the whole page. */
function RestoringScreen() {
  return (
    <div className="grid h-full place-items-center" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-4 text-ink-faint">
        <BrandMark size={52} />
        <span className="text-[12px] font-bold uppercase tracking-[0.05em]">Загрузка</span>
      </div>
    </div>
  );
}
